import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { defineMessage, defineMessages, FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { usePropertyLocales } from "../../components/property-locale-provider";
import * as google_type_money_pb from "../../gen/google/type/money_pb";
import schema from "../../gen/schema/witbooking.backoffice.loyalty.tier.v1/LoyaltyTier.json";
import translationsSchema from "../../gen/schema/witbooking.backoffice.loyalty.tier.v1/LoyaltyTierTranslation.json";
import { LoyaltyBrandAPIPromiseClient } from "../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_grpc_web_pb";
import {
  BookAndJoinType,
  ConfigureBookAndJoinRequest,
  ConfigureRegistrationTextRequest,
  RegistrationTextType,
} from "../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { LoyaltyTierAPIPromiseClient } from "../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_grpc_web_pb";
import {
  CreateLoyaltyTierRequest,
  DeleteLoyaltyTierRequest,
  GetLoyaltyTierRequest,
  GetLoyaltyTiersByBrandRequest,
  LoyaltyConstantTier,
  LoyaltyDerivedTier,
  LoyaltyPointsRule,
  LoyaltyPromotedDiscount,
  LoyaltyRule,
  LoyaltyRuleSet,
  LoyaltySpentAmountRule,
  LoyaltyTier,
  LoyaltyTierTranslation,
  LoyaltyTransactionsAmountRule,
  PromoteLoyaltyTierRequest,
  ReorderLoyaltyTierRequest,
  UpdateLoyaltyTierRequest,
} from "../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_pb";
import actions from "../../lib/actions";
import { useUserAuthInfo } from "../../lib/auth";
import { getLanguageFromFullLocale } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { useGrpcClient, useLoyaltyGrpcCall } from "../../lib/grpc";
import { useApiClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { inputFilter } from "../shared/crud/filter-widgets";
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns, TableFilters } from "../shared/crud/list";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { RendererProps } from "../shared/renderers";
import * as TableRenderers from "../shared/renderers/table";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import { AmountType } from "../shared/widgets/amountType";
import { ArrayAsString } from "../shared/widgets/arrayAsString";
import { ColorPicker } from "../shared/widgets/colorPicker";
import { HtmlEditorWidget } from "../shared/widgets/html";
import { LoyaltyTierConditions } from "../shared/widgets/loyaltyTierConditions";

import { translationFieldConfig } from "../shared/widgets/translation";
import { TIER_TYPE } from "../witadmin/Common/constants";
import SelectGroup from "../witadmin/CRUD/Common/SelectGroup";

import { FormContainer } from "./form-style";

const loyaltyTierMessages = defineMessages({
  entityName: {
    id: "loyaltyTier.entityName",
    defaultMessage: "Loyalty Tiers",
  },
  name: {
    id: "loyaltyTier.name",
    defaultMessage: "Name",
  },
  ticker: {
    id: "loyaltyTier.ticker",
    defaultMessage: "ticker",
  },
  highlightedNameTop: {
    id: "loyaltyTier.highlightedNameTop",
    defaultMessage: "highlightedNameTop",
  },
  highlightedNameBottom: {
    id: "loyaltyTier.highlightedNameBottom",
    defaultMessage: "highlightedNameBottom",
  },
  description: {
    id: "loyaltyTier.description",
    defaultMessage: "description",
  },
  benefitDescription: {
    id: "loyaltyTier.benefitDescription",
    defaultMessage: "benefitDescription",
  },
  highlightedTextTop: {
    id: "loyaltyTier.highlightedTextTop",
    defaultMessage: "highlightedTextTop",
  },
  highlightedTextBottom: {
    id: "loyaltyTier.highlightedTextBottom",
    defaultMessage: "highlightedTextBottom",
  },
  rgbaColor: {
    id: "loyaltyTier.rgbaColor",
    defaultMessage: "rgbaColor",
  },
  promoCodesList: {
    id: "loyaltyTier.promoCodesList",
    defaultMessage: "promoCodesList",
  },
  amount: {
    id: "loyaltyTier.amount",
    defaultMessage: "amount",
  },
  constant: {
    id: "loyaltyTier.constant",
    defaultMessage: "constant",
  },
  percentage: {
    id: "loyaltyTier.percentage",
    defaultMessage: "percentage",
  },
  discount: {
    id: "loyaltyTier.discount",
    defaultMessage: "discount",
  },
  promotedTier: {
    id: "loyaltyTier.promotedTier",
    defaultMessage: "Promoted Tier",
  },
  configureBookAndJoin: {
    id: "loyaltyTier.configureBookAndJoin",
    defaultMessage: "configureBookAndJoin",
  },
  ["bookAndJoin" + BookAndJoinType.DISABLED]: {
    id: "loyaltyTier.bookAndJoinDisabledLabel",
    defaultMessage: "Opt in",
  },
  ["bookAndJoin" + BookAndJoinType.OPT_IN]: {
    id: "loyaltyTier.bookAndJoinOptInLabel",
    defaultMessage: "Opt in",
  },
  ["bookAndJoin" + BookAndJoinType.OPT_OUT]: {
    id: "loyaltyTier.bookAndJoinOptOutLabel",
    defaultMessage: "Opt out",
  },
  ["registrationText" + RegistrationTextType.WITH_SAVINGS]: {
    id: "loyaltyTier.withSavings",
    defaultMessage: "Show the amount saved and club benefits text",
  },
  ["registrationText" + RegistrationTextType.WITHOUT_SAVINGS]: {
    id: "loyaltyTier.withoutSavings",
    defaultMessage: "Show only the club benefits text",
  },
  promotedTierTooltip: {
    id: "loyaltyTier.promotedTierTooltip",
    defaultMessage: "Promoted Tier Tooltip",
  },
  save: {
    id: "default.save",
    defaultMessage: "Save",
  },

  highlightedNameTopHelpText: {
    id: "loyaltyTier.highlightedNameTopHelpText",
    defaultMessage: "Help Text",
  },
  highlightedNameBottomHelpText: {
    id: "loyaltyTier.highlightedNameBottomHelpText",
    defaultMessage: "Help Text",
  },
  highlightedTextTopHelpText: {
    id: "loyaltyTier.highlightedTextTopHelpText",
    defaultMessage: "Help Text",
  },
  highlightedTextBottomHelpText: {
    id: "loyaltyTier.highlightedTextBottomHelpText",
    defaultMessage: "Help Text",
  },
  descriptionHelpText: {
    id: "loyaltyTier.descriptionHelpText",
    defaultMessage: "Help Text",
  },
  benefitDescriptionHelpText: {
    id: "loyaltyTier.benefitDescriptionHelpText",
    defaultMessage: "Help Text",
  },
  promoCodesListHelpText: {
    id: "loyaltyTier.promoCodesListHelpText",
    defaultMessage: "Help Text",
  },
  rgbaColorHelpText: {
    id: "loyaltyTier.rgbaColorHelpText",
    defaultMessage: "Help Text",
  },
  discountHelpText: {
    id: "loyaltyTier.discountHelpText",
    defaultMessage: "Help Text",
  },
  tickerHelpText: {
    id: "loyaltyTier.tickerHelpText",
    defaultMessage: "Help Text",
  },
  AdvancedOptions: {
    id: "loyaltyTier.AdvancedOptions",
    defaultMessage: "Advanced Options",
  },
  registrationModalText: {
    id: "loyaltyTier.registrationModalText",
    defaultMessage: "Registration Modal Text",
  },
});

const tableHeaderMessages = defineMessages({
  ticker: { id: "loyaltyTier.ticker", defaultMessage: "Level ticker" },
  translationsList: { id: "loyaltyTier.translationsList", defaultMessage: "translationsList" },
  highlightedNameTop: {
    id: "loyaltyTier.highlightedNameTopShort",
    defaultMessage: "Name L1",
  },
  highlightedNameBottom: {
    id: "loyaltyTier.highlightedNameBottomShort",
    defaultMessage: "Name L2",
  },
  description: { id: "loyaltyTier.descriptionShort", defaultMessage: "Level description" },
  benefitDescription: {
    id: "loyaltyTier.benefitDescriptionShort",
    defaultMessage: "Benefits description",
  },
  highlightedTextTop: {
    id: "loyaltyTier.highlightedTextTopShort",
    defaultMessage: "Benefits summary (line 1)",
  },
  highlightedTextBottom: {
    id: "loyaltyTier.highlightedTextBottomShort",
    defaultMessage: "Benefits summary (line 2)",
  },
  rgbaColor: { id: "loyaltyTier.rgbaColor", defaultMessage: "Color" },
  promoCodesList: { id: "loyaltyTier.promoCodesListShort", defaultMessage: "Promo Codes" },
  promotedDiscount: { id: "loyaltyTier.discountShort", defaultMessage: "Promoted Discount" },
  amount: { id: "loyaltyTier.discountShort", defaultMessage: "Promoted Discount" },
  name: { id: "loyaltyTier.name", defaultMessage: "Name" },
});

const messages = {
  ...defaultCrudMessages,
  ...loyaltyTierMessages,
  patternValidationFailed: {
    ".ticker": defineMessage({
      id: "loyaltyTier.tickerPatternFailed",
      defaultMessage: "Must be uppercase. Doesn't allow blank spaces or special characters",
    }),
  },
  tableHeaders: tableHeaderMessages,
  deleteDisabledDescription: defineMessage({
    id: "loyaltyTier.deleteDisabledDescription",
    defaultMessage: "This loyalty tier has linked inventory lines, so it can't be deleted.",
  }),
};

interface LoyaltyTierFilters {
  ticker: string;
  amount: string;
  rgbaColor: string;
  promoCodesList: string;
  description: string;
  benefitDescription: string;
  highlightedNameTop: string;
  highlightedNameBottom: string;
}

function setCommonTier(
  obj: LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject,
  result: LoyaltyConstantTier | LoyaltyDerivedTier
) {
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setTranslationsList(obj.translationsList.map(translationFromObject));
  // @ts-ignore
  const createdAt = new proto.google.protobuf.Timestamp();
  createdAt.setNanos(obj.createdAt?.nanos);
  createdAt.setSeconds(obj.createdAt?.seconds);
  // @ts-ignore
  const updatedAt = new proto.google.protobuf.Timestamp();
  updatedAt.setNanos(obj.updatedAt?.nanos);
  updatedAt.setSeconds(obj.updatedAt?.seconds);

  result.setCreatedAt(createdAt);
  result.setUpdatedAt(updatedAt);
  result.setRgbaColor(obj.rgbaColor);
  if (obj.promotedDiscount) {
    result.setPromotedDiscount(promotedDiscountFromObject(obj.promotedDiscount));
  }
  result.setPromoCodesList(obj.promoCodesList);
  result.setExternalSystemId(obj.externalSystemId);
}

function constantTierFromObject(obj: LoyaltyConstantTier.AsObject): LoyaltyConstantTier {
  const result = new LoyaltyConstantTier();
  setCommonTier(obj, result);
  return result;
}
function derivedTierFromObject(obj: LoyaltyDerivedTier.AsObject): LoyaltyDerivedTier {
  const result = new LoyaltyDerivedTier();
  setCommonTier(obj, result);
  if (obj.conditions) {
    result.setConditions(conditionsFromObject(obj.conditions));
  }
  return result;
}

function conditionsFromObject(obj: LoyaltyRuleSet.AsObject): LoyaltyRuleSet {
  const result = new LoyaltyRuleSet();
  result.setConditionsList(obj.conditionsList?.map(conditionFromObject));
  result.setAllConditionsMustBeMet(obj.allConditionsMustBeMet);

  return result;
}

function conditionFromObject(obj: LoyaltyRule.AsObject): LoyaltyRule {
  const result = new LoyaltyRule();
  if (obj.pointsCondition) {
    result.setPointsCondition(pointsConditionFromObject(obj.pointsCondition));
  }
  if (obj.spentAmountCondition) {
    result.setSpentAmountCondition(spentAmountCondition(obj.spentAmountCondition));
  }
  if (obj.purchasesAmountCondition) {
    result.setPurchasesAmountCondition(
      purchasesAmountConditionFromObject(obj.purchasesAmountCondition)
    );
  }
  return result;
}

function purchasesAmountConditionFromObject(
  obj: LoyaltyTransactionsAmountRule.AsObject
): LoyaltyTransactionsAmountRule {
  const result = new LoyaltyTransactionsAmountRule();
  result.setAmount(obj.amount);
  result.setContextKeysList(obj.contextKeysList);

  return result;
}

function pointsConditionFromObject(obj: LoyaltyPointsRule.AsObject): LoyaltyPointsRule {
  const result = new LoyaltyPointsRule();
  result.setAmount(obj.amount);
  result.setContextKeysList(obj.contextKeysList);

  return result;
}

function spentAmountCondition(obj: LoyaltySpentAmountRule.AsObject): LoyaltySpentAmountRule {
  const result = new LoyaltySpentAmountRule();
  if (obj.amount) {
    result.setAmount(amountFromObject(obj.amount));
  }
  result.setContextKeysList(obj.contextKeysList);

  return result;
}
function amountFromObject(obj: google_type_money_pb.Money.AsObject): google_type_money_pb.Money {
  const result = new google_type_money_pb.Money();
  result.setCurrencyCode(obj.currencyCode);
  result.setUnits(obj.units);
  result.setNanos(obj.nanos);

  return result;
}

function promotedDiscountFromObject(
  obj: LoyaltyPromotedDiscount.AsObject
): LoyaltyPromotedDiscount {
  const result = new LoyaltyPromotedDiscount();
  result.setAmount(obj.amount);
  result.setType(obj.type);

  return result;
}

function translationFromObject(obj: LoyaltyTierTranslation.AsObject): LoyaltyTierTranslation {
  const result = new LoyaltyTierTranslation();
  result.setLocale(obj.locale);
  result.setHighlightedNameTop(obj.highlightedNameTop);
  result.setHighlightedNameBottom(obj.highlightedNameBottom);
  result.setHighlightedTextTop(obj.highlightedTextTop);
  result.setHighlightedTextBottom(obj.highlightedTextBottom);
  result.setName(obj.name);
  result.setDescription(obj.description);
  result.setBenefitDescription(obj.benefitDescription);
  return result;
}

let modifiedSchema = schema;
//Schema fixes
//Fixing badly generated refs
// @ts-ignore
modifiedSchema.properties.derivedTier.properties.promotedDiscount.$ref =
  "#/definitions/witbooking.backoffice.loyalty.tier.v1.LoyaltyPromotedDiscount";
// @ts-ignore
modifiedSchema.properties.constantTier.properties.promotedDiscount.$ref =
  "#/definitions/witbooking.backoffice.loyalty.tier.v1.LoyaltyPromotedDiscount";

//Otherwise translations titles don't work properly
// @ts-ignore
modifiedSchema.properties.derivedTier.properties.translations.items = translationsSchema;
// @ts-ignore
modifiedSchema.properties.constantTier.properties.translations.items = translationsSchema;

//Adding missing validation pattern
// @ts-ignore
modifiedSchema.properties.derivedTier.properties.promoCodes.items.pattern = "^[a-zA-Z0-9*_-]+$";
// @ts-ignore
modifiedSchema.properties.constantTier.properties.promoCodes.items.pattern = "^[a-zA-Z0-9*_-]+$";

//Schema generates incorrectly for proto.google.protobuf.Timestamp() and causes validate error
// @ts-ignore
modifiedSchema.properties.derivedTier.properties.createdAt.type = "object";
// @ts-ignore
modifiedSchema.properties.constantTier.properties.createdAt.type = "object";

// @ts-ignore
modifiedSchema.properties.derivedTier.properties.updatedAt.type = "object";
// @ts-ignore
modifiedSchema.properties.constantTier.properties.updatedAt.type = "object";

function ColoredStringRenderer({ value }: RendererProps<string>) {
  return <div style={{ color: value || "black" }}>{value}</div>;
}

const StyledPromotedTierSelector = styled.div`
  margin-top: 20px;
  & select {
    width: 400px;
  }
  margin-bottom: 50px;
`;

const StyledButton = styled(Button)`
  min-width: 75px;
`;

const PromotedTierTooltip = styled.div`
  margin-bottom: 1rem;
`;

const AdvancedOptions = styled.div`
  color: #5ab5e0;
  text-decoration: underline;
  cursor: pointer;
  margin-bottom: 10px;
`;

const regularFilterColumns = ["ticker", "promoCodesList"];
const translationsFilterColumns = [
  "translationsList.name",
  "translationsList.description",
  "translationsList.benefitDescription",
  "translationsList.highlightedNameTop",
  "translationsList.highlightedNameBottom",
];

const columnVisibilityExtensions = [
  "translationsList.promotedDiscount",
  "translationsList.name",
  "translationsList.benefitDescription",
  "promoCodesList",
].map(it => ({ columnName: it, togglingEnabled: false }));

function LoyaltyTierCrud({
  propertyTicker,
  tierType,
}: {
  propertyTicker: string;
  tierType: string;
}) {
  const user = useUserAuthInfo();
  const intl = useIntl();
  const loyaltyBrand = useLoyaltyBrand() || undefined;
  const brand = loyaltyBrand?.ticker || "";
  const tierClient = useGrpcClient(LoyaltyTierAPIPromiseClient);
  const brandClient = useGrpcClient(LoyaltyBrandAPIPromiseClient);
  const httpClientCache = useApiClient();

  const getLoyaltyTiersByBrand = useLoyaltyGrpcCall(tierClient, tierClient.getLoyaltyTiersByBrand);
  const getLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.getLoyaltyTier);
  const createLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.createLoyaltyTier);
  const updateLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.updateLoyaltyTier);
  const deleteLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.deleteLoyaltyTier);
  const promoteLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.promoteLoyaltyTier);
  const reorderLoyaltyTier = useLoyaltyGrpcCall(tierClient, tierClient.reorderLoyaltyTier);
  const configureBookAndJoin = useLoyaltyGrpcCall(brandClient, brandClient.configureBookAndJoin);
  const configureRegistrationText = useLoyaltyGrpcCall(
    brandClient,
    brandClient.configureRegistrationText
  );

  const [bookAndJoinType, setBookAndJoinType] = useState(
    loyaltyBrand?.bookAndJoinType ?? BookAndJoinType.DISABLED
  );
  const [registrationTextType, setRegistrationTextType] = useState<
    RegistrationTextType | undefined
  >();
  const [promotedID, setPromotedID] = useState("");
  const [originalPromotedID, setOriginalPromotedID] = useState("");
  const [allTiers, setAllTiers] = useState<
    (LoyaltyDerivedTier.AsObject | LoyaltyConstantTier.AsObject | undefined)[] | undefined
  >(undefined);
  const [constantTiers, setConstantTiers] = useState<
    (LoyaltyConstantTier.AsObject | undefined)[] | undefined
  >();
  const [derivedTiers, setDerivedTiers] = useState<
    (LoyaltyDerivedTier.AsObject | undefined)[] | undefined
  >();
  const [promoteID, setPromoteID] = useState(uuidv4());
  const [savingPromoted, setSavingPromoted] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [lastTierType, setLastTierType] = useState<string>("");
  const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLButtonElement>) => {
    setPromotedID(event?.target?.value);
  }, []);

  const handleRegistrationTextChange = useCallback(
    (event: React.ChangeEvent<HTMLButtonElement>) => {
      setRegistrationTextType((parseInt(event?.target?.value) as unknown) as RegistrationTextType);
    },
    []
  );

  const handleChangeBookAndJoinType = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setBookAndJoinType((parseInt(event?.target?.value) as unknown) as BookAndJoinType);
  }, []);

  useEffect(() => {
    if (!registrationTextType && loyaltyBrand?.registrationTextType) {
      setRegistrationTextType(loyaltyBrand?.registrationTextType);
    }
  }, [registrationTextType, loyaltyBrand]);

  useEffect(() => {
    if (tierType && tierType !== lastTierType) {
      setForceUpdate(forceUpdate + 1);
    }
    setLastTierType(tierType);
  }, [forceUpdate, lastTierType, tierType]);

  useEffect(() => {
    if (!loyaltyBrand) {
      return;
    }
    setBookAndJoinType(loyaltyBrand.bookAndJoinType);
  }, [loyaltyBrand]);

  useEffect(() => {
    const asyncFun = async () => {
      if (!allTiers) {
        const request = new GetLoyaltyTiersByBrandRequest();
        request.setBrandTicker(brand);
        const response = await getLoyaltyTiersByBrand(request);
        if (response) {
          const res = response.toObject();
          setPromotedID(res.promotedTier);
          setOriginalPromotedID(res.promotedTier);
          setAllTiers(
            res.tiersList.map(it => (it.constantTier ? it.constantTier : it.derivedTier))
          );
          setConstantTiers(res.tiersList.map(it => it.constantTier).filter(it => it));
          setDerivedTiers(res.tiersList.map(it => it.derivedTier).filter(it => it));
        }
      }
    };
    asyncFun();
  }, [tierType, allTiers, brand, getLoyaltyTiersByBrand]);

  const onGetAll = useCallback(async () => {
    const request = new GetLoyaltyTiersByBrandRequest();
    request.setBrandTicker(brand);
    const response = await getLoyaltyTiersByBrand(request);
    if (tierType === TIER_TYPE.CONSTANT) {
      return response
        .getTiersList()
        .filter(it => it.hasConstantTier())
        .map(it => it.getConstantTier())
        .filter(it => it)
        .map(it => it?.toObject() as LoyaltyConstantTier.AsObject);
    } else {
      return response
        .getTiersList()
        .filter(it => it.hasDerivedTier())
        .map(it => it.getDerivedTier())
        .filter(it => it)
        .map(it => it?.toObject() as LoyaltyDerivedTier.AsObject);
    }
    // Needed to reload tier list on promoted tier change
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLoyaltyTiersByBrand, tierType, brand, promoteID]);

  const onGetOne = useCallback(
    async (id: string) => {
      const request = new GetLoyaltyTierRequest();
      request.setBrandTicker(brand);
      request.setId(id);
      const response = (await getLoyaltyTier(request)).getTier();
      if (response) {
        return tierType === TIER_TYPE.CONSTANT
          ? (response.getConstantTier()?.toObject() as LoyaltyConstantTier.AsObject)
          : (response.getDerivedTier()?.toObject() as LoyaltyDerivedTier.AsObject);
      } else {
        throw new Error("Unexpected reply obtaining loyaltyTier");
      }
    },
    [brand, getLoyaltyTier, tierType]
  );

  const onCreate: (
    entity: LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject
  ) => void = useCallback(
    async entity => {
      const loyaltyTier = new LoyaltyTier();
      if (tierType === TIER_TYPE.CONSTANT) {
        loyaltyTier.setConstantTier(constantTierFromObject(entity));
      } else {
        loyaltyTier.setDerivedTier(derivedTierFromObject(entity));
      }

      const request = new CreateLoyaltyTierRequest();
      request.setBrandTicker(brand);
      request.setTier(loyaltyTier);
      await createLoyaltyTier(request);
    },
    [tierType, brand, createLoyaltyTier]
  );

  const onUpdate: (
    entity: LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject
  ) => Promise<void> = useCallback(
    async entity => {
      const loyaltyTier = new LoyaltyTier();
      if (tierType === TIER_TYPE.CONSTANT) {
        loyaltyTier.setConstantTier(constantTierFromObject(entity));
      } else {
        loyaltyTier.setDerivedTier(derivedTierFromObject(entity));
      }

      const request = new UpdateLoyaltyTierRequest();
      request.setBrandTicker(brand);
      request.setTier(loyaltyTier);
      await updateLoyaltyTier(request);
    },

    [tierType, brand, updateLoyaltyTier]
  );

  const onDelete = useCallback(
    async (id: string) => {
      const request = new DeleteLoyaltyTierRequest();
      request.setBrandTicker(brand);
      request.setId(id);
      await deleteLoyaltyTier(request);
    },
    [deleteLoyaltyTier, brand]
  );

  const disableDelete = useCallback(
    (value: LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject) =>
      value.inUse && "loyaltyTier.inUse",
    []
  );

  const allowedLocales = usePropertyLocales();

  const formConfiguration: FormConfiguration<
    LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject
  > = useMemo(
    () => ({
      __fieldOrder: [
        "id",
        "ticker",
        "promotedDiscount",
        "rgbaColor",
        "translationsList",
        "promoCodesList",
        "*",
      ],
      __useProtoStyleArrays: true,
      id: {
        default: () => uuidv4(),
        hidden: true,
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({
            id: "loyaltyTier.ticker",
            defaultMessage: "Ticker",
          }),
          helpText: defineMessage({
            id: "loyaltyTier.tickerHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      conditions: {
        fieldWidget: LoyaltyTierConditions,
      },
      createdAt: {
        hidden: true,
      },
      updatedAt: {
        hidden: true,
      },
      externalSystemId: {
        hidden: true,
      },
      rgbaColor: {
        fieldWidget: ColorPicker,
        messages: {
          label: defineMessage({
            id: "loyaltyTier.rgbaColor",
            defaultMessage: "Level color",
          }),
          helpText: defineMessage({
            id: "loyaltyTier.rgbaColorHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      promotedDiscount: {
        fieldWidget: AmountType,
        messages: {
          label: defineMessage({
            id: "loyaltyTier.discount",
            defaultMessage: "Discount",
          }),
          helpText: defineMessage({
            id: "loyaltyTier.discountHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["name", "*"],
          locale: {
            hidden: true,
          },
          name: {
            messages: {
              label: defineMessage({
                id: "loyaltyTier.name",
                defaultMessage: "Name",
              }),
            },
          },
          highlightedNameTop: {
            messages: {
              label: defineMessage({
                id: "loyaltyTier.highlightedNameTop",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.highlightedNameTopHelpText",
                defaultMessage: "HelpText",
              }),
            },
          },
          highlightedNameBottom: {
            messages: {
              label: defineMessage({
                id: "loyaltyTier.highlightedNameBottom",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.highlightedNameBottomHelpText",
                defaultMessage: "HelpText",
              }),
            },
          },
          highlightedTextTop: {
            messages: {
              label: defineMessage({
                id: "loyaltyTier.highlightedTextTop",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.highlightedTextTopHelpText",
                defaultMessage: "HelpText",
              }),
            },
            hidden: true,
          },
          highlightedTextBottom: {
            messages: {
              label: defineMessage({
                id: "loyaltyTier.highlightedTextBottom",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.highlightedTextBottomHelpText",
                defaultMessage: "HelpText",
              }),
            },
            hidden: true,
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "loyaltyTier.description",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.descriptionHelpText",
                defaultMessage: "HelpText",
              }),
            },
          },
          benefitDescription: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "loyaltyTier.benefitDescription",
                defaultMessage: "Description",
              }),
              helpText: defineMessage({
                id: "loyaltyTier.benefitDescriptionHelpText",
                defaultMessage: "HelpText",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({ id: "loyaltyTier.translations", defaultMessage: "Translations" }),
        },
      },
      promoCodesList: {
        fieldWidget: ArrayAsString,
        messages: {
          label: defineMessage({
            id: "loyaltyTier.promoCodesList",
            defaultMessage: "Description",
          }),
          helpText: defineMessage({
            id: "loyaltyTier.promoCodesListHelpText",
            defaultMessage: "HelpText",
          }),
        },
      },
      inUse: {
        hidden: true,
      },
    }),
    [allowedLocales]
  );

  const columns: TableColumns<LoyaltyConstantTier.AsObject> = {
    ticker: {
      renderer: TableRenderers.StringRenderer,
      width: 140,
    },
    promotedDiscount: {
      children: {
        amount: {
          renderer: mapRenderer(StringRenderer, (v, intl) => {
            if (!v) {
              return undefined;
            }
            return `${v.amount}${v.type === 0 ? "%" : ""}`;
          }),
          width: 140,
        },
      },
    },
    rgbaColor: {
      renderer: ColoredStringRenderer,
      width: 140,
    },
    translationsList: {
      children: {
        name: {
          renderer: mapRenderer(StringRenderer, (v, intl) => {
            if (!v || !v.length) return undefined;
            const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
            return current?.name || v[0].name;
          }),
        },
        description: {
          renderer: mapRenderer(
            StringRenderer,
            (v, intl) => {
              if (!v || !v.length) return undefined;
              const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
              return current?.description || v[0].description;
            },
            { isHtml: true }
          ),
        },
        benefitDescription: {
          renderer: mapRenderer(
            StringRenderer,
            (v, intl) => {
              if (!v || !v.length) return undefined;
              const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
              return current?.benefitDescription || v[0].benefitDescription;
            },
            { isHtml: true }
          ),
        },
        highlightedNameTop: {
          renderer: mapRenderer(StringRenderer, (v, intl) => {
            if (!v || !v.length) return undefined;
            const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
            return current?.highlightedNameTop || v[0].highlightedNameTop;
          }),
        },
        highlightedNameBottom: {
          renderer: mapRenderer(StringRenderer, (v, intl) => {
            if (!v || !v.length) return undefined;
            const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
            return current?.highlightedNameBottom || v[0].highlightedNameBottom;
          }),
        },
      },
    },

    promoCodesList: {
      renderer: mapRenderer(StringRenderer, (v, intl) => {
        if (!v || !v.length) {
          return undefined;
        }
        return v.toString();
      }),
      width: 140,
    },
  };

  const CSVRenderers = {
    promotedDiscount: (values: LoyaltyPromotedDiscount.AsObject) => {
      return `${values.amount}${values.type === 0 ? "%" : ""}`;
    },
  };

  const translationsFilters = useMemo(
    () =>
      translationsFilterColumns.reduce(
        (accum: TableFilters<LoyaltyConstantTier.AsObject, LoyaltyTierFilters>, curr) => ({
          ...accum,
          [curr]: {
            filterFn: (data: any, filter: any, intl: any) => {
              const current = data.find(
                (t: any) => t.locale === getLanguageFromFullLocale(intl.locale)
              );
              return current[curr.split(".")[1]].toLowerCase().includes(filter.toLowerCase());
            },
            widget: inputFilter,
          },
        }),
        {}
      ),
    []
  );

  const promotedDiscountFilter = useMemo(
    () => ({
      "promotedDiscount.amount": {
        filterFn: (data: any, filter: any) => {
          const amount = String(data?.amount);
          return amount.includes(filter.toLowerCase());
        },
        widget: inputFilter,
      },
    }),
    []
  );

  const regularTableFilters: TableFilters<
    LoyaltyConstantTier.AsObject,
    LoyaltyTierFilters
  > = useMemo(
    () =>
      regularFilterColumns.reduce(
        (accum, curr) => ({
          ...accum,
          [curr]: {
            filterFn: (data: any, filter: any) =>
              typeof data === "string"
                ? data.toLowerCase().includes(filter.toLowerCase())
                : data.join().toLowerCase().includes(filter.toLowerCase()),
            widget: inputFilter,
          },
        }),
        {}
      ),
    []
  );

  const filters = useMemo(
    () => ({ ...regularTableFilters, ...translationsFilters, ...promotedDiscountFilter }),
    [regularTableFilters, translationsFilters, promotedDiscountFilter]
  );

  const handlePromote = useCallback(async () => {
    setSavingPromoted(true);
    if (!loyaltyBrand) {
      return;
    }
    if (bookAndJoinType !== loyaltyBrand.bookAndJoinType) {
      const request = new ConfigureBookAndJoinRequest();
      request.setBrandId(loyaltyBrand.id);
      request.setBookAndJoinType(bookAndJoinType);
      await configureBookAndJoin(request);
      await regenPropertyCache(httpClientCache, propertyTicker);
    }
    if (registrationTextType && registrationTextType !== loyaltyBrand.registrationTextType) {
      const request = new ConfigureRegistrationTextRequest();
      request.setBrandId(loyaltyBrand.id);
      request.setRegistrationTextType(registrationTextType);
      await configureRegistrationText(request);
      await regenPropertyCache(httpClientCache, propertyTicker);
    }
    if (promotedID !== originalPromotedID && bookAndJoinType === BookAndJoinType.DISABLED) {
      const request = new PromoteLoyaltyTierRequest();
      request.setBrandTicker(brand);
      if (promotedID) {
        request.setId(promotedID);
        request.setPromote(true);
      } else {
        request.setId(originalPromotedID);
        request.setPromote(false);
      }
      setPromoteID(uuidv4());

      await promoteLoyaltyTier(request);
    }
    setSavingPromoted(false);
  }, [
    loyaltyBrand,
    bookAndJoinType,
    registrationTextType,
    promotedID,
    originalPromotedID,
    configureBookAndJoin,
    httpClientCache,
    propertyTicker,
    configureRegistrationText,
    brand,
    promoteLoyaltyTier,
  ]);

  const handleReorder = useCallback(
    async (id: string, after?: string) => {
      const request = new ReorderLoyaltyTierRequest();
      request.setBrandTicker(brand);
      request.setId(id);
      request.setAfter(after || "");

      await reorderLoyaltyTier(request);
      setForceUpdate(forceUpdate + 1); // mini hack to re-render the data and make update works properly
    },
    [brand, reorderLoyaltyTier, forceUpdate]
  );

  const toggleShowAdvancedOptions = useCallback(() => {
    setShowAdvancedOptions(!showAdvancedOptions);
  }, [showAdvancedOptions]);

  const promotedTierSelector = useMemo(() => {
    const type = tierType === TIER_TYPE.CONSTANT ? constantTiers : derivedTiers;
    if (tierType === TIER_TYPE.CONSTANT) {
      return null;
    }
    return (
      <StyledPromotedTierSelector>
        {user && user?.isWitAdmin ? (
          <>
            <FormattedMessage {...messages.configureBookAndJoin} />
            <SelectGroup
              value={bookAndJoinType}
              field={{
                fieldName: "type",
                widget: "selector",
                optionDisplay: {
                  [BookAndJoinType.DISABLED]: {
                    label: intl.formatMessage(messages["bookAndJoin" + BookAndJoinType.DISABLED]),
                  },
                  [BookAndJoinType.OPT_IN]: {
                    label: intl.formatMessage(messages["bookAndJoin" + BookAndJoinType.OPT_IN]),
                  },
                  [BookAndJoinType.OPT_OUT]: {
                    label: intl.formatMessage(messages["bookAndJoin" + BookAndJoinType.OPT_OUT]),
                  },
                },
              }}
              onChange={({ event }) => handleChangeBookAndJoinType(event)}
              errors={undefined}
              additionalFields={undefined}
              className={""}
            />
          </>
        ) : null}

        <FormattedMessage {...messages.promotedTier} />
        <SelectGroup
          value={promotedID}
          field={{
            fieldName: "type",
            widget: "selector",
            disabled: bookAndJoinType !== BookAndJoinType.DISABLED,
            optionDisplay:
              (typeof type === "object" &&
                type.reduce(
                  (accum, curr) => (curr ? { [curr.id]: { label: curr.ticker }, ...accum } : accum),
                  {
                    "": { label: "--" },
                  }
                )) ||
              {},
          }}
          onChange={({ event }) => handleChange(event)}
          errors={undefined}
          additionalFields={undefined}
          className={""}
        />
        <PromotedTierTooltip>
          <FormattedMessage {...messages.promotedTierTooltip} />
        </PromotedTierTooltip>
        <AdvancedOptions onClick={toggleShowAdvancedOptions}>
          <FormattedMessage {...messages.AdvancedOptions} />
        </AdvancedOptions>
        {showAdvancedOptions && (
          <div>
            <FormattedMessage {...messages.registrationModalText} />
            <SelectGroup
              value={registrationTextType}
              field={{
                fieldName: "registrationTextType",
                widget: "selector",
                optionDisplay: {
                  [RegistrationTextType.WITH_SAVINGS]: {
                    label: intl.formatMessage(
                      messages["registrationText" + RegistrationTextType.WITH_SAVINGS]
                    ),
                  },
                  [RegistrationTextType.WITHOUT_SAVINGS]: {
                    label: intl.formatMessage(
                      messages["registrationText" + RegistrationTextType.WITHOUT_SAVINGS]
                    ),
                  },
                },
              }}
              onChange={({ event }) => handleRegistrationTextChange(event)}
              errors={undefined}
              additionalFields={undefined}
              className={""}
            />
          </div>
        )}
        <StyledButton variant="primary" onClick={handlePromote}>
          {savingPromoted ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            <FormattedMessage {...messages.save} />
          )}
        </StyledButton>
      </StyledPromotedTierSelector>
    );
  }, [
    tierType,
    constantTiers,
    derivedTiers,
    user,
    bookAndJoinType,
    intl,
    promotedID,
    toggleShowAdvancedOptions,
    showAdvancedOptions,
    registrationTextType,
    handlePromote,
    savingPromoted,
    handleChangeBookAndJoinType,
    handleChange,
    handleRegistrationTextChange,
  ]);

  if (allowedLocales === undefined) {
    return null;
  }

  if (tierType === TIER_TYPE.CONSTANT) {
    // @ts-ignore
    modifiedSchema = { ...modifiedSchema, ...modifiedSchema.properties.constantTier };
  } else {
    // @ts-ignore
    modifiedSchema = { ...modifiedSchema, ...modifiedSchema.properties.derivedTier };
  }

  // not using translations for managerName because filter storage won't work on language change
  return (
    <>
      {brand && (
        <SimpleCrud<LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject, LoyaltyTierFilters>
          formConfiguration={formConfiguration}
          schema={modifiedSchema}
          tableColumns={columns}
          tableFilters={filters}
          formContainer={FormContainer}
          messages={messages}
          onGetAll={onGetAll}
          onGetOne={onGetOne}
          columnVisibility={true}
          columnVisibilityExtensions={columnVisibilityExtensions}
          managerName={"loyalty"}
          onCreate={onCreate}
          canCreate={
            tierType === TIER_TYPE.CONSTANT
              ? actions.CreateConstantLoyaltyTiers
              : actions.CreateDerivedLoyaltyTiers
          }
          onUpdate={onUpdate}
          onDelete={onDelete}
          section={
            tierType === TIER_TYPE.CONSTANT
              ? SECTION.LOYALTY_CONSTANT_TIERS
              : SECTION.LOYALTY_DERIVED_TIERS
          }
          CSVRenderers={CSVRenderers}
          disableDelete={disableDelete}
          listHeight={400}
          additionalFooterContent={promotedTierSelector}
          onReorder={tierType === TIER_TYPE.DERIVED ? handleReorder : undefined}
          forceLoad={forceUpdate}
        />
      )}
    </>
  );
}

export default withAuthorizationVerifier(LoyaltyTierCrud);
